<template>
  <!-- using key here as a workaround for missing two-way data binding in form fields -->
  <div :key="leadId" class="page">
    <header class="header">
      <h1 class="title">{{ $t('add-new-request') }}:</h1>
      <flight-component :flight="flight" class="flight-card" read-only />
    </header>
    <text-component
      ref="firstName"
      v-model="form.firstName.value"
      :placeholder="$t('add-first-name')"
      :errors="$t(form.firstName.errors)"
      name="first-name"
      type="text"
      capitalize
      @input="validateIsRequired(form.firstName)"
    />
    <text-component
      ref="lastName"
      v-model="form.lastName.value"
      :placeholder="$t('add-last-name')"
      :errors="$t(form.firstName.errors)"
      name="last-name"
      type="text"
      maxlength="50"
      capitalize
    />
    <phone-component
      :label="$t('phone-number')"
      v-model="form.phone.value"
      :errors="$t(form.phone.errors)"
      :placeholder="$t('add-phone')"
      name="phone"
    />

    <textarea-component
      v-model="form.description.value"
      :label="$t('description')"
      :placeholder="$t('add-description')"
      :errors="$t(form.description.errors)"
      name="description"
      type="text"
      capitalize
    />

    <div class="two-columns">
      <number-component
        :label="$t('potential-cases')"
        :placeholder="$t('add-potential-cases')"
        v-model="form.potentialCases.value"
        :errors="$t(form.potentialCases.errors)"
        name="potential-cases"
        type="number"
        min="1"
        @input="validateIsRequired(form.potentialCases)"
      />
      <contact-component
        :label="$t('prefferred-date-of-first-contact')"
        :errors="$t(form.deadline.errors)"
        v-model="form.deadline.value"
        :disabled-dates="{ to: yesterday }"
        @input="validateDateTime(form.deadline, 'error-both-required')"
      />
    </div>

    <radio
      v-model="form.language.value"
      :default-value="form.language.value"
      :options="languages"
      :errors="$t(form.language.errors)"
      :title="$t('preferred-contact-language')"
      @input="validateIsRequired(form.language)"
    />

    <div class="buttons">
      <button-component
        :class="{ 'btn--disabled': loadingLeave || loadingNew }"
        class="btn--out"
        green
        @click="handleSubmitAndLeave"
      >
        <template>
          <div v-if="!loadingLeave">
            {{ $t('save-and-leave') }}
          </div>
          <div v-if="loadingLeave" class="loader">
            <loader white />
          </div>
        </template>
      </button-component>
      <div class="inner-buttons">
        <button-component
          :class="{ 'btn--disabled': loadingLeave || loadingNew }"
          square
          white
          @click="handleBack"
        >
          <div class="arrow arrow--left" />
        </button-component>
        <button-component
          :class="{ 'btn--disabled': loadingLeave || loadingNew }"
          class="btn-save"
          @click="handleSubmitAndAddNew"
        >
          <template>
            <div v-if="!loadingNew">
              {{ $t('save-and-add-new') }}
            </div>
            <div v-if="loadingNew" class="loader">
              <loader white />
            </div>
          </template>
        </button-component>
      </div>
    </div>
    <error
      v-for="(nonFieldError, index) in nonFieldErrors"
      :key="index"
      :errors="nonFieldError"
      center
    />
    <modal
      v-if="showModal"
      :message="showModal"
      :message-time="3000"
      @hide-modal="showModal = false"
    />
  </div>
</template>

<script>
import FlightComponent from '@/components/Flight'
import ButtonComponent from '@/components/Button'
import PhoneComponent from '@/components/form/fields/Phone'
import NumberComponent from '@/components/form/fields/Number'
import TextComponent from '@/components/form/fields/Text'
import TextareaComponent from '@/components/form/fields/Textarea'
import Radio from '@/components/form/fields/Radio'
import ContactComponent from '@/components/form/fields/Contact'
import Loader from '@/components/Loader'
import Error from '@/components/Error'
import { validateIsRequired, validateDateTime } from '@/utils/validator'
import { mapGetters } from 'vuex'
import { nanoid } from 'nanoid'
import Modal from '@/components/Modal'

const createForm = () => ({
  firstName: {
    value: '',
    errors: ''
  },
  lastName: {
    value: '',
    errors: ''
  },
  phone: {
    value: '',
    errors: ''
  },
  potentialCases: {
    value: '',
    errors: ''
  },
  deadline: {
    value: {},
    errors: ''
  },
  language: {
    value: 'pl',
    errors: ''
  },
  description: {
    value: '',
    errors: ''
  }
})

export default {
  components: {
    Date,
    TextareaComponent,
    FlightComponent,
    ButtonComponent,
    PhoneComponent,
    Radio,
    NumberComponent,
    ContactComponent,
    TextComponent,
    Loader,
    Error,
    Modal
  },
  data() {
    return {
      validateIsRequired,
      validateDateTime,
      loadingLeave: false,
      loadingNew: false,
      flight: {},
      nonFieldErrors: [],
      redirect: '',
      showModal: false,
      form: createForm(),
      leadId: nanoid()
    }
  },
  computed: {
    ...mapGetters({ formLanguage: 'getLang' }),
    languages() {
      return [
        {
          value: 'pl',
          label: this.$t('polish')
        },
        {
          value: 'en',
          label: this.$t('english')
        },
        {
          value: 'cs',
          label: this.$t('czech')
        },
        {
          value: 'ru',
          label: this.$t('russian')
        }
      ]
    },
    yesterday() {
      return new Date(Date.now() - 8640000)
    }
  },
  mounted() {
    if (localStorage.getItem('flight')) {
      this.flight = JSON.parse(localStorage.getItem('flight'))
    } else {
      this.$router.push({ name: 'dashboard' })
    }
  },
  methods: {
    camelized(s) {
      return s.replace(/_./g, x => x.toUpperCase()[1])
    },
    validate() {
      return {
        validatePotentialCases: validateIsRequired(this.form.potentialCases),
        validateFirstName: validateIsRequired(this.form.firstName),
        validateLanguage: validateIsRequired(this.form.language),
        validateDeadline: validateDateTime(this.form.deadline)
      }
    },
    sendForm() {
      const deadline = this.form.deadline.value.value
      const flightCode = this.flight.flightNumber.split(' ')
      const payload = {
        first_name: this.form.firstName.value,
        last_name: this.form.lastName.value,
        flight: this.flight.id,
        phone: this.form.phone.value,
        potential_cases: this.form.potentialCases.value
          ? parseInt(this.form.potentialCases.value)
          : undefined,
        deadline: deadline ? deadline.toISOString() : undefined,
        language: this.form.language.value,
        flight_number: this.flight.flightNumber,
        airline_code: flightCode[0],
        departure_airport_code: this.flight.departureAirportIATA,
        arrival_airport_code: this.flight.arrivalAirportIATA,
        departure_date: this.flight.departureDate,
        travel_disruption: this.flight.travelDisruption,
        disruption_reason: this.flight.disruptionReason,
        delay: this.flight.delayTimeAmount,
        compensation: this.flight.compensationAmount,
        transfer_airport: this.flight.intermediateAirportIATA1 || undefined,
        description: this.form.description.value
      }

      this.$store
        .dispatch('createLead', {
          data: payload,
          locale: this.form.language.value,
          id: this.leadId
        })
        .then(() => {
          const phoneNumber = payload.phone
          const flightNumber = payload.flight_number

          if (this.redirect) {
            this.$router.replace({
              name: this.redirect,
              params: {
                status: this.$t('passenger-has-been-added', {
                  flight: flightNumber,
                  phone: phoneNumber
                }),
                statusTime: 3000
              }
            })
          } else {
            this.showModal = this.$t('passenger-has-been-added', {
              flight: flightNumber,
              phone: phoneNumber
            })
            setTimeout(() => this.resetForm(), 3000)
          }

          this.loadingNew = false
          this.loadingLeave = false
        })
        .catch(error => {
          if (process.env.VUE_APP_ENABLE_ROLLBAR) {
            this.$rollbar.error('Add case: ', error)
          }

          this.loadingNew = false
          this.loadingLeave = false
          const errors = error.response && error.response.data

          if (
            errors &&
            !Array.isArray(errors) &&
            error.response.status === 400
          ) {
            const mapErrors = []

            Object.entries(errors).forEach(entry => {
              const [key, value] = entry
              if (!value) return
              const camelizedKey = this.camelized(key)

              if (this.form[camelizedKey]) {
                this.form[camelizedKey].errors = value[0]
              } else {
                const val =
                  Array.isArray(value) && key !== 'detail'
                    ? `${key}: ${value[0]}`
                    : value

                mapErrors.push(val)
              }
            })

            // TODO: Fix non fields error type (string instead of object with data and detail)
            this.nonFieldErrors = mapErrors || []
          } else if (
            (errors && Array.isArray(errors)) ||
            (errors && error.response.status === 418)
          ) {
            this.nonFieldErrors = errors || []
          } else {
            this.nonFieldErrors = [
              'Wystąpił problem po stronie serwera. Proszę spróbować później.'
            ]
          }
        })
    },
    handleSubmitAndLeave() {
      this.loadingLeave = true
      this.redirect = 'dashboard'
      this.submitForm()
    },
    handleSubmitAndAddNew() {
      this.loadingNew = true
      this.submitForm()
    },
    handleBack() {
      localStorage.setItem('flight', '')
      this.$router.push({ name: 'dashboard' })
    },
    submitForm() {
      const validationPassed = !Object.values(this.validate()).includes(false)
      if (validationPassed) {
        this.sendForm()
      } else {
        this.loadingNew = false
        this.loadingLeave = false
      }
    },
    resetForm() {
      this.loadingLeave = false
      this.loadingNew = false
      this.nonFieldErrors = []
      this.redirect = ''
      this.showModal = false
      this.form = createForm()
      this.leadId = nanoid()
    }
  }
}
</script>

<style lang="scss" scoped>
.description {
  margin: 15px 0;
}

.description-line {
  font-size: 20px;
  margin: 0.6em 0 0;
  line-height: 1.4;

  strong {
    font-weight: 800;
  }
}

.text {
  font-size: 16px;
  margin: 8px 0 0;

  &--agreement {
    margin-top: 20px;
  }
}

.buttons {
  margin-top: 40px;
  @include media-up(sm) {
    display: flex;
  }
}

.inner-buttons {
  display: flex;

  @include media-up(sm) {
    width: 320px;
    margin-right: 10px;
  }
}

.btn--out {
  width: 100%;
  height: 60px;

  @include media-down(sm) {
    margin-bottom: 22px;
  }

  @include media-up(sm) {
    order: 1;
    width: 0;
    flex: 1;
  }
}

.btn-save {
  flex: 1;
  margin-left: 10px !important;
}

.clear {
  padding: 9px 27px;
  font-size: 16px;
  background: #f2f2f2;
  border: 2px solid #000;
  border-top: none;
  width: 100%;
}

.link {
  color: #000;

  &:hover {
    text-decoration: none;
  }
}

.header {
  @include media-up(sm) {
    display: flex;
    justify-content: space-between;
  }
}

.title {
  flex: 1;
  margin: 0;

  @include media-up(sm) {
    max-width: 300px;
  }
}
.flight-card {
  width: 250px;
  margin-bottom: 10px;
  padding: 16px 16px;

  @include media-down(sm) {
    width: 100%;
    margin-top: 30px;
  }
}

.two-columns {
  display: grid;
  grid-template-columns: 1fr;

  @include media-up(md) {
    grid-template-columns: 2fr 3fr;
    grid-gap: 16px;
  }
}
</style>
